import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useRefreshSession, useSession } from '../../lib/session';
import { CenterScreen, Logo, Spinner } from '@vatman/ui';

/** Accepts a board, team, organization or tracker project invitation, asking to sign in first if needed. */
export function InvitePage({ kind }: { kind: 'board' | 'team' | 'org' | 'project' }) {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const session = useSession();
  const refresh = useRefreshSession();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  const started = useRef(false);
  const here = `${location.pathname}`;

  useEffect(() => {
    if (!session.data?.user || started.current) return;
    started.current = true;
    const path =
      kind === 'board' ? `/board-invites/${token}/accept` : kind === 'team' ? `/team-invites/${token}/accept` : kind === 'project' ? `/project-invites/${token}/accept` : `/org-invites/${token}/accept`;
    api
      .post<{ boardId?: string; teamId?: string; orgId?: string; projectId?: string }>(path)
      .then(async (res) => {
        await refresh();
        if (res.boardId) navigate(`/b/${res.boardId}`, { replace: true });
        else if (res.projectId) navigate(`/t/${res.projectId}`, { replace: true });
        else navigate(res.orgId ? `/?org=${res.orgId}` : '/', { replace: true });
      })
      .catch(() => setFailed(true));
  }, [session.data?.user, kind, token, navigate, refresh]);

  if (session.isLoading) {
    return (
      <CenterScreen>
        <Spinner />
      </CenterScreen>
    );
  }
  if (!session.data?.user) {
    return (
      <CenterScreen>
        <Logo size={40} />
        <p className="muted">{t('invite.needLogin')}</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link className="btn primary" to={`/register?next=${encodeURIComponent(here)}`}>
            {t('auth.register')}
          </Link>
          <Link className="btn" to={`/login?next=${encodeURIComponent(here)}`}>
            {t('auth.login')}
          </Link>
        </div>
      </CenterScreen>
    );
  }
  return (
    <CenterScreen>
      <Logo size={40} />
      {failed ? (
        <>
          <p className="error-text">{t('invite.failed')}</p>
          <Link className="btn" to="/">
            {t('board.toDashboard')}
          </Link>
        </>
      ) : (
        <>
          <Spinner />
          <p className="muted">{t('invite.accepting')}</p>
        </>
      )}
    </CenterScreen>
  );
}
